import React, { useCallback, useEffect, useState } from "react";
import { FiArrowLeft, FiEdit2, FiPlusCircle } from "react-icons/fi";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as inventoryService from "../../services/inventoryService";
import { getRole } from "../../components/Apicall";
import ExportMenu from "../../components/InventoryExport/ExportMenu";
import LowStockBadge from "./LowStockBadge";
import ItemFormDrawer from "./ItemFormDrawer";
import StockAdjustModal from "./StockAdjustModal";
import ItemStockPanel from "./stock/ItemStockPanel";
import { StockMovePanel } from "./stock/StockOverview";
import { useInvSettings } from "./stock/invUi";
import { formatReportDate } from "../../utils/dateFormat";
import { formatDecimal } from "../../utils/numberFormat";
import { getVendorCurrency } from "../../utils/currency";

const HISTORY_COLUMNS = [
  { key: "created_at", label: "Date", format: "date" },
  { key: "txn_type", label: "Type" },
  { key: "qty_change", label: "Qty change", format: "number" },
  { key: "balance_after", label: "Balance after", format: "number" },
  { key: "unit_cost", label: "Unit cost", format: "currency" },
  { key: "reason", label: "Reason" },
  { key: "performed_by_name", label: "Performed by" },
  { key: "display_booking_id", label: "Booking" },
];

const TXN_BADGE = {
  PURCHASE_IN: { label: "Purchase In", color: "#27AE60", bg: "#E6F9F0" },
  BOOKING_USAGE: { label: "Booking Usage", color: "#2563EB", bg: "#EFF6FF" },
  ADJUSTMENT_IN: { label: "Adjustment In", color: "#0EA5E9", bg: "#E0F2FE" },
  ADJUSTMENT_OUT: { label: "Adjustment Out", color: "#D97706", bg: "#FEF3C7" },
  RETURN: { label: "Return", color: "#6B7280", bg: "#F3F4F6" },
  WASTAGE: { label: "Wastage", color: "var(--button-color)", bg: "#FFF3F3" },
};

const thStyle = {
  background: "#F9FAFB",
  fontWeight: "600",
  fontSize: "13px",
  color: "#374151",
  padding: "12px 16px",
  borderBottom: "1px solid #E5E7EB",
  whiteSpace: "nowrap",
  textAlign: "left",
};

const tdStyle = {
  padding: "12px 16px",
  fontSize: "14px",
  color: "#374151",
  borderBottom: "1px solid #F3F4F6",
  verticalAlign: "middle",
};

const backBtnStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  border: "1px solid #E5E7EB",
  borderRadius: "8px",
  padding: "6px 12px",
  background: "#fff",
  color: "#6B7280",
  fontSize: "13px",
  fontWeight: "600",
  cursor: "pointer",
  marginBottom: "12px",
};

const rowActionBtnStyle = {
  border: "1px solid #E5E7EB",
  borderRadius: "8px",
  padding: "6px 12px",
  color: "#6B7280",
  background: "#fff",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
  fontSize: "13px",
  fontWeight: "600",
};

const primaryActionBtnStyle = {
  ...rowActionBtnStyle,
  background: "var(--button-color)",
  border: "none",
  color: "#fff",
};

function InventoryItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isAdmin = getRole() === "3";

  const [item, setItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showAdjust, setShowAdjust] = useState(false);
  const inv = useInvSettings();
  // With warehouses on, or for a batch / serial item, stock moves need the warehouse and the batches / serials.
  const [tracked, setTracked] = useState(false);
  const richMove = !!(inv.data && inv.data.multi_warehouse) || tracked;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [itemRes, historyRes] = await Promise.all([
        inventoryService.getItem(id),
        inventoryService.getItemHistory(id, { limit: 100 }),
      ]);
      setItem(itemRes?.data || null);
      setHistory(historyRes?.data || []);
    } catch (err) {
      toast.error(err.message || "Could not load item");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <div className="p-4">Loading…</div>;
  if (!item) return <div className="p-4">Item not found.</div>;

  return (
    <div className="p-3">
      <button type="button" style={backBtnStyle} onClick={() => navigate("/inventory")}>
        <FiArrowLeft size={14} /> Back to Inventory
      </button>

      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h4 className="mb-1">
            {item.name} <span className="text-muted" style={{ fontSize: 14 }}>({item.sku})</span>
          </h4>
          <div className="d-flex align-items-center gap-2">
            <LowStockBadge currentQty={item.current_qty} reorderLevel={item.reorder_level} />
            <span className="text-muted" style={{ fontSize: 13 }}>
              {formatDecimal(item.current_qty)} {item.unit_of_measure} in stock · {item.category_name || "Uncategorized"}
            </span>
          </div>
        </div>
        {isAdmin && (
          <div className="d-flex gap-2">
            <button type="button" style={rowActionBtnStyle} onClick={() => setShowForm(true)}>
              <FiEdit2 size={14} /> Edit Item
            </button>
            <button type="button" style={primaryActionBtnStyle} onClick={() => setShowAdjust(true)}>
              <FiPlusCircle size={14} /> Stock-In / Adjust
            </button>
          </div>
        )}
      </div>

      <ItemStockPanel
        itemId={id}
        retailPrice={item.selling_price}
        canManage={isAdmin || !!(inv.data && inv.data.can && inv.data.can.manage)}
        onChanged={load}
        onLoaded={(x) => setTracked(x.tracking !== "NONE")}
      />

      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">History Timeline</h6>
        <ExportMenu
          rows={history}
          columns={HISTORY_COLUMNS}
          filenameBase={`item_${item.sku}_history`}
          onExportPdf={(html) => inventoryService.exportItemHistoryPdf(id, html)}
        />
      </div>

      <div
        style={{
          overflowX: "auto",
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
          <thead>
            <tr>
              {HISTORY_COLUMNS.map((c) => (
                <th key={c.key} style={thStyle}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {history.length === 0 && (
              <tr>
                <td colSpan={HISTORY_COLUMNS.length} style={{ ...tdStyle, textAlign: "center", color: "#9CA3AF" }}>
                  No stock movements yet.
                </td>
              </tr>
            )}
            {history.map((row) => {
              const txnBadge = TXN_BADGE[row.txn_type] || { label: row.txn_type, color: "#6B7280", bg: "#F3F4F6" };
              return (
                <tr
                  key={row.id}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#F9FAFB")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  style={{ transition: "background 0.15s" }}
                >
                  <td style={tdStyle}>{formatReportDate(row.created_at)}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "3px 12px",
                        borderRadius: "9999px",
                        fontSize: "12px",
                        fontWeight: "600",
                        background: txnBadge.bg,
                        color: txnBadge.color,
                        border: `1px solid ${txnBadge.color}`,
                      }}
                    >
                      {txnBadge.label}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, color: row.qty_change < 0 ? "var(--button-color)" : "#27AE60", fontWeight: "600" }}>
                    {row.qty_change > 0 ? `+${formatDecimal(row.qty_change)}` : formatDecimal(row.qty_change)}
                  </td>
                  <td style={tdStyle}>{formatDecimal(row.balance_after)}</td>
                  <td style={tdStyle}>{getVendorCurrency()} {formatDecimal(row.unit_cost)}</td>
                  <td style={tdStyle}>{row.reason || "—"}</td>
                  <td style={tdStyle}>{row.performed_by_name || row.performed_by_email || "—"}</td>
                  <td style={tdStyle}>{row.display_booking_id || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ItemFormDrawer show={showForm} item={item} onHide={() => setShowForm(false)} onSaved={load} />
      {richMove ? (
        showAdjust ? <StockMovePanel item={item} onHide={() => setShowAdjust(false)} onDone={load} /> : null
      ) : (
        <StockAdjustModal show={showAdjust} item={item} onHide={() => setShowAdjust(false)} onPosted={load} />
      )}
    </div>
  );
}

export default InventoryItemDetail;
